import React, { useState } from 'react';
import { MapPin, Clock, Calendar, Flame, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Navigation from './Navigation';
import Footer from './Footer';
import OTASignupForm from './OTASignupForm';
import SmsCoachButton from './shared/SmsCoachButton';
import { offSeasonTraining } from '../mock';

const RECRUIT_PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 100%)',
  color: '#000',
  padding: '0.5rem 1.15rem',
  borderRadius: '999px',
  fontWeight: 900,
  fontSize: '0.82rem',
  letterSpacing: '2px',
  marginBottom: '1rem',
  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.35)',
};

const RECRUIT_BULLETS_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '0.6rem 1rem',
  maxWidth: '820px',
  margin: '1.5rem auto 0',
  textAlign: 'left',
  listStyle: 'none',
  padding: 0,
};

const TrainingPage = () => {
  const [showSignupForm, setShowSignupForm] = useState(false);

  return (
    <div className="page-container">
      <Navigation />

      <main className="main-content">
        <section className="page-hero" data-testid="training-hero">
          <div className="container" style={{ textAlign: 'center' }}>
            <span style={RECRUIT_PILL_STYLE} data-testid="training-recruiting-pill">
              <Flame size={16} /> {offSeasonTraining.tagline || 'ACTIVELY RECRUITING'}
            </span>
            <h1 className="page-title">{offSeasonTraining.title}</h1>
            <p className="page-subtitle">{offSeasonTraining.description}</p>

            {Array.isArray(offSeasonTraining.recruitingBullets) && (
              <ul style={RECRUIT_BULLETS_STYLE} data-testid="training-recruiting-bullets">
                {offSeasonTraining.recruitingBullets.map((b) => (
                  <li
                    key={b}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      color: '#f0f0f0',
                      fontSize: '0.95rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <CheckCircle2 size={18} style={{ color: '#FFD700', flexShrink: 0, marginTop: '2px' }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="training-details-section">
          <div className="container">
            <div className="training-grid">
              <Card className="training-card">
                <CardContent>
                  <div className="training-icon">
                    <Calendar size={32} />
                  </div>
                  <h3>Schedule</h3>
                  <p className="training-value">{offSeasonTraining.schedule.dates}</p>
                  <p className="training-dates">{offSeasonTraining.schedule.days}</p>
                  <p className="training-dates">{offSeasonTraining.schedule.time}</p>
                  <p className="training-dates" style={{ marginTop: '0.75rem', color: '#FFD700', fontWeight: 600 }}>
                    {offSeasonTraining.ageGroups}
                  </p>
                </CardContent>
              </Card>

              <Card className="training-card">
                <CardContent>
                  <div className="training-icon">
                    <Clock size={32} />
                  </div>
                  <h3>Details</h3>
                  <p className="training-value">Cost: {offSeasonTraining.details.cost}</p>
                  <p className="training-dates">{offSeasonTraining.details.requirements}</p>
                </CardContent>
              </Card>

              <Card className="training-card">
                <CardContent>
                  <div className="training-icon">
                    <MapPin size={32} />
                  </div>
                  <h3>Location</h3>
                  <p className="training-value">{offSeasonTraining.location.name}</p>
                  <p className="training-address">{offSeasonTraining.location.address}</p>
                  <a
                    href="https://www.google.com/maps/place/Randallstown+High+School/@39.3673,-76.8025,17z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="map-link"
                  >
                    View on Map
                  </a>
                </CardContent>
              </Card>
            </div>

            <div className="training-cta">
              <h2>Come Practice With Us — Bring a Friend</h2>
              <p>
                All age groups, all skill levels. Your first practice is free — walk up, meet the coaches,
                and see if the Panthers are the right fit for your athlete.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  className="btn-primary btn-large"
                  onClick={() => setShowSignupForm(true)}
                  data-testid="training-signup-cta"
                >
                  Reserve My Spot
                </Button>
                {offSeasonTraining.smsContact && (
                  <SmsCoachButton
                    phone={offSeasonTraining.smsContact.phone}
                    message={offSeasonTraining.smsContact.prefilledMessage}
                    label={`Text ${offSeasonTraining.smsContact.coachName}`}
                    testId="training-sms-cta"
                  />
                )}
              </div>
              {offSeasonTraining.smsContact && (
                <p style={{ marginTop: '0.75rem', color: '#a0a0a0', fontSize: '0.85rem' }}>
                  Prefer to text? {offSeasonTraining.smsContact.coachName} · {offSeasonTraining.smsContact.displayPhone}
                </p>
              )}
            </div>
          </div>
        </section>

        <OTASignupForm
          isOpen={showSignupForm}
          onClose={() => setShowSignupForm(false)}
        />
      </main>

      <Footer />
    </div>
  );
};

export default TrainingPage;
